import { useEffect, useState } from 'react';

import { fetchPostRepaymentRegisterRepaymentMethod } from '@iwoca/lapi-client/edge';
import { Button, ErrorMessage, Label, Modal } from '@iwoca/orion';
import { Frames, CardNumber, ExpiryDate, Cvv, CardType } from 'frames-react';
import { useForm } from 'react-hook-form';

import { getFramesConfig } from './frames.config';
import { useStateKey } from '../../../iwQueryClient/useStateKey';
import { PaymentMethodAuthenticationModal } from '../../modals/paymentMethodAuthenticationModal/PaymentMethodAuthenticationModal';

type TFormValues = {
    billingPostcode: string;
    cardholderName: string;
};

export const CheckoutLinkDebitCard = ({
    onSuccess
}: {
    onSuccess: () => void;
}) => {
    const stateKey = useStateKey();
    const {
        register,
        handleSubmit,
        formState: { errors, isSubmitting },
        getValues,
        reset
    } = useForm<TFormValues>({
        defaultValues: {
            billingPostcode: '',
            cardholderName: ''
        }
    });
    const [is3DSModalOpen, setIs3DSModalOpen] = useState(false);
    const [checkout3DSecureRedirectUrl, setCheckout3DSecureRedirectUrl] =
        useState<string | null>(null);
    // Used to show a generic error message if Frames.submitCard fails
    const [framesError, setFramesError] = useState(false);

    const onSubmit = async () => {
        setFramesError(false);

        try {
            const { token, card_type } = await Frames.submitCard();
            const response = await fetchPostRepaymentRegisterRepaymentMethod({
                stateKey: stateKey,
                body: {
                    data: {
                        token,
                        card_type: card_type as Uppercase<CardType> // Type for card_type is wrong - thanks Checkout.com :(
                    }
                }
            });

            const redirect_url = response.data.checkout_3ds_redirect_link;

            if (redirect_url) {
                setCheckout3DSecureRedirectUrl(redirect_url);
                setIs3DSModalOpen(true);
            }

            // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
            setFramesError(true);
            return;
        }

        // Reset the frames elements
        Frames.init();
        // Reset other form values
        reset();

        onSuccess();
    };

    useEffect(() => {
        const handle3DSCompleteEvent = (event: MessageEvent) => {
            if (event.data === '3DS-authentication-complete') {
                setIs3DSModalOpen(false);
            } else if (event.data === '3DS-authentication-failed') {
                setIs3DSModalOpen(false);
                setFramesError(true);
            }
        };

        window.addEventListener('message', handle3DSCompleteEvent);
        return () =>
            window.removeEventListener('message', handle3DSCompleteEvent);
    }, []);

    return (
        <>
            {checkout3DSecureRedirectUrl && (
                <Modal
                    isOpen={is3DSModalOpen}
                    onClose={() => setIs3DSModalOpen(false)}
                >
                    <PaymentMethodAuthenticationModal
                        authenticationIframeURL={checkout3DSecureRedirectUrl}
                    />
                </Modal>
            )}
            <form
                data-testid="checkout-link-card-form"
                onSubmit={handleSubmit(onSubmit)}
            >
                <div className="grid gap-l pt-xl min-m:grid-cols-3">
                    <Frames
                        config={getFramesConfig({
                            billingPostcode: getValues('billingPostcode'),
                            cardholderName: getValues('cardholderName')
                        })}
                    >
                        <div>
                            <Label
                                htmlFor="cardNumber"
                                className="mb-m font-bold"
                            >
                                Card number
                            </Label>
                            <CardNumber
                                style={{ height: '50px' }}
                                data-testid="checkout-card-number"
                            />
                        </div>

                        <div>
                            <Label
                                htmlFor="expiryDate"
                                className="mb-m font-bold"
                            >
                                Expiry date
                            </Label>
                            <ExpiryDate
                                style={{ height: '50px' }}
                                data-testid="checkout-expiry-date"
                            />
                        </div>

                        <div>
                            <Label htmlFor="cvv" className="mb-m font-bold">
                                CVV
                            </Label>
                            <Cvv
                                style={{ height: '50px' }}
                                data-testid="checkout-cvv"
                            />
                        </div>

                        <div className="flex flex-col">
                            <Label
                                className="mb-m font-bold"
                                htmlFor="billingPostcode"
                            >
                                Billing postcode
                            </Label>
                            {/* eslint-disable-next-line iwoca/input-a11y */}
                            <input
                                id="billingPostcode"
                                placeholder="e.g. N6 6NG"
                                className="h-[50px] border border-[#e9e9e9] bg-structure-100 p-[13px] font-[18px] focus:outline-primary-40"
                                {...register('billingPostcode', {
                                    required: true
                                })}
                            />
                            {errors.billingPostcode && (
                                <ErrorMessage className="mt-xs">
                                    Billing postcode is required
                                </ErrorMessage>
                            )}
                        </div>

                        <div className="flex flex-col">
                            <Label
                                className="mb-m font-bold"
                                htmlFor="cardholderName"
                            >
                                Cardholder name
                            </Label>
                            <input
                                type="text"
                                id="cardholderName"
                                placeholder="John Smith"
                                className="h-[50px] border border-[#e9e9e9] bg-structure-100 p-[13px] font-[18px] focus:outline-primary-40"
                                {...register('cardholderName', {
                                    required: true
                                })}
                            />
                            {errors.cardholderName && (
                                <ErrorMessage className="mt-xs">
                                    Cardholder name is required
                                </ErrorMessage>
                            )}
                        </div>
                    </Frames>
                </div>
                <div className="mt-xl flex w-full flex-col items-center gap-m">
                    <Button
                        className="w-full max-w-[300px]"
                        loading={isSubmitting}
                    >
                        Add your card
                    </Button>
                    {framesError && (
                        <ErrorMessage className="mt-xs">
                            An error occurred when attempting to link your card
                        </ErrorMessage>
                    )}
                </div>
            </form>
        </>
    );
};
